/**
 * IndexedDB 键值存储
 * 仅用于体积较大的动态配置缓存；UUID 等小字段仍走 localStorage
 * 不可用 / 超时 / 写入失败时由调用方回落 localStorage
 */

const DB_NAME = 'xt_tracker'
const DB_VERSION = 1
const STORE_NAME = 'kv'

/** 读路径超时：避免卡住 init */
const IDB_READ_TIMEOUT = 200
/** 写路径超时：后台写入，可稍长 */
const IDB_WRITE_TIMEOUT = 2000

let dbPromise: Promise<IDBDatabase> | null = null

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined'
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(label)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (!isIndexedDbAvailable()) {
      reject(new Error('IndexedDB unavailable'))
      return
    }

    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION)
    } catch (error) {
      reject(error)
      return
    }

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }

    request.onsuccess = () => {
      const db = request.result
      db.onversionchange = () => {
        db.close()
        dbPromise = null
      }
      resolve(db)
    }

    request.onerror = () => {
      reject(request.error || new Error('IndexedDB open failed'))
    }
  }).catch((error) => {
    dbPromise = null
    throw error
  })

  return dbPromise
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

/**
 * 读取字符串值；IndexedDB 不可用或超时时 reject，由调用方回落
 */
export async function idbGetItem(key: string): Promise<string | null> {
  const db = await withTimeout(openDatabase(), IDB_READ_TIMEOUT, 'IndexedDB open timeout')
  const tx = db.transaction(STORE_NAME, 'readonly')
  const request = tx.objectStore(STORE_NAME).get(key)
  const value = await withTimeout(requestToPromise(request), IDB_READ_TIMEOUT, 'IndexedDB get timeout')
  if (value == null) return null
  return typeof value === 'string' ? value : String(value)
}

/**
 * 写入字符串值
 */
export async function idbSetItem(key: string, value: string): Promise<void> {
  const db = await withTimeout(openDatabase(), IDB_WRITE_TIMEOUT, 'IndexedDB open timeout')
  const tx = db.transaction(STORE_NAME, 'readwrite')
  tx.objectStore(STORE_NAME).put(value, key)
  await withTimeout(transactionDone(tx), IDB_WRITE_TIMEOUT, 'IndexedDB set timeout')
}

/**
 * 删除指定 key
 */
export async function idbRemoveItem(key: string): Promise<void> {
  const db = await withTimeout(openDatabase(), IDB_WRITE_TIMEOUT, 'IndexedDB open timeout')
  const tx = db.transaction(STORE_NAME, 'readwrite')
  tx.objectStore(STORE_NAME).delete(key)
  await withTimeout(transactionDone(tx), IDB_WRITE_TIMEOUT, 'IndexedDB remove timeout')
}

/**
 * 测试或销毁时重置连接缓存
 */
export function resetIndexedDbConnection(): void {
  dbPromise = null
}
